"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";
import { TVOverlay } from "@/components/tv-overlay";

interface BGTRewardsCardProps {
  rewards: bigint | null | undefined;
  formattedRewards: string;
  hasRewards: boolean;
  isClaiming: boolean;
  onClaim: () => void;
  variant?: "blue" | "green" | "yellow";
  className?: string;
  isConnected?: boolean;
  onConnect?: () => void;
}

const VARIANT_STYLES = {
  blue: {
    border: "linear-gradient(135deg, #60a5fa, #3b82f6)",
    shadow: "0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
  },
  green: {
    border: "linear-gradient(135deg, #1cc75e, #16a34a)",
    shadow: "0 4px 12px rgba(28, 199, 94, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
  },
  yellow: {
    border: "linear-gradient(135deg, #facc15, #eab308)",
    shadow: "0 4px 12px rgba(250, 204, 21, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
  },
};

export function BGTRewardsCard({
  rewards,
  formattedRewards,
  hasRewards,
  isClaiming,
  onClaim,
  variant = "blue",
  className = "",
  isConnected = true,
  onConnect,
}: BGTRewardsCardProps) {
  const styles = VARIANT_STYLES[variant];
  const showRewards = isConnected && hasRewards;

  return (
    <div
      className={`relative overflow-hidden p-2 ${className}`}
      style={{
        background: styles.border,
        boxShadow: styles.shadow,
      }}
    >
      <div
        className="relative overflow-hidden rounded-sm"
        style={{
          background: "linear-gradient(135deg, #0a0a0b 0%, #141416 100%)",
          boxShadow: `
            inset 0 3px 12px rgba(0, 0, 0, 0.9),
            inset 0 -2px 4px rgba(0, 0, 0, 0.7),
            inset 0 1px 0 rgba(0, 0, 0, 1)
          `.trim(),
        }}
      >
        <TVOverlay intensity="medium" />
        <div className="relative z-10 flex items-center justify-between p-4">
          <div className="flex items-center gap-2.5">
            {/* BGT Icon - grayed out when no rewards */}
            <div className={!showRewards ? "opacity-40" : ""}>
              <Image
                src="/token/bgt.png"
                alt="BGT"
                width={32}
                height={32}
                className="drop-shadow-[0_2px_4px_rgba(250,204,21,0.5)]"
              />
            </div>
            <div>
              <p className="font-pressStart2P text-[8px] uppercase text-white/60">
                BGT Rewards
              </p>
              <p className={`font-pressStart2P text-sm ${showRewards ? "text-yellow-400" : "text-white/40"}`}>
                {!isConnected ? "0.00" : (rewards && rewards > 0n ? formattedRewards : "0.00")}
              </p>
            </div>
          </div>

          {/* Button or status */}
          {showRewards ? (
            <Button
              variant="action-yellow"
              size="sm"
              className="h-8 px-4 text-[9px]"
              onClick={onClaim}
              disabled={isClaiming}
            >
              {isClaiming ? "Claiming..." : "Claim"}
            </Button>
          ) : (
            <span className="font-pressStart2P text-[8px] text-muted-foreground/60 uppercase">
              No Rewards
            </span>
          )}
        </div>
      </div>
    </div>
  );
}